import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";

function TournamentDetail() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pendingApproval, setPendingApproval] = useState(false);

  useEffect(() => {
    fetchTournamentDetail();
  }, [tournamentId]);

  useEffect(() => {
    const fetchPending = async () => {
      if (!user || !tournament) return;
      const q = query(
        collection(db, "tournamentRegistrations"),
        where("userId", "==", user.id),
        where("tournamentId", "==", tournament.id),
        where("status", "==", "pending")
      );
      const snap = await getDocs(q);
      setPendingApproval(!snap.empty);
    };
    fetchPending();
  }, [user, tournament]);

  const fetchTournamentDetail = async () => {
    try {
      const docRef = doc(db, "tournaments", tournamentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setTournament({ id: docSnap.id, ...docSnap.data() });
      } else {
        setError("대회를 찾을 수 없습니다.");
      }
    } catch (error) {
      console.error("대회 정보 로딩 실패:", error);
      setError("대회 정보를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status, registrationEnd) => {
    const statusConfig = {
      'registration': { 
        label: '모집중', 
        color: '#10b981', 
        bg: '#d1fae5',
        icon: '📝'
      },
      'active': { 
        label: '진행중', 
        color: '#3b82f6', 
        bg: '#dbeafe',
        icon: '⚡'
      },
      'completed': { 
        label: '완료', 
        color: '#6b7280', 
        bg: '#f3f4f6',
        icon: '✅'
      },
      'upcoming': { 
        label: '예정', 
        color: '#f59e0b', 
        bg: '#fef3c7',
        icon: '⏰'
      }
    };

    // 모집 마감일 체크
    if (status === 'registration' && registrationEnd) {
      const now = new Date();
      const endDate = registrationEnd.toDate ? registrationEnd.toDate() : new Date(registrationEnd);
      if (now > endDate) {
        return {
          label: '모집마감',
          color: '#ef4444',
          bg: '#fee2e2',
          icon: '🚫'
        };
      }
    }

    return statusConfig[status] || statusConfig.upcoming;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntil = (date) => {
    if (!date) return null;
    const d = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diffTime = d - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const canRegister = () => {
    if (!user || !tournament) return false;
    if (tournament.status !== 'registration') return false;
    if (tournament.participants?.some(p => p.userId === user.id)) return false;
    
    const now = new Date();
    const regEnd = tournament.registrationEnd?.toDate ? tournament.registrationEnd.toDate() : new Date(tournament.registrationEnd);
    return now <= regEnd;
  };

  const isParticipating = () => {
    return tournament?.participants?.some(p => p.userId === user?.id);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: 18,
        color: '#6b7280'
      }}>
        🏆 대회 정보 로딩중...
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div style={{
        maxWidth: 600,
        margin: '40px auto',
        padding: 40,
        textAlign: 'center',
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>❌</div>
        <h3 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12, color: '#ef4444' }}>
          {error || "대회를 찾을 수 없습니다"}
        </h3>
        <button
          onClick={() => navigate('/tournaments')}
          style={{
            background: '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            padding: '12px 24px',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          대회 목록으로 돌아가기
        </button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(tournament.status, tournament.registrationEnd);
  const daysUntilStart = getDaysUntil(tournament.startDate);
  const daysUntilRegEnd = getDaysUntil(tournament.registrationEnd);

  return (
    <div style={{
      maxWidth: 900,
      margin: '40px auto',
      padding: '0 20px'
    }}>
      {/* 헤더 카드 */}
      <div style={{
        background: `linear-gradient(135deg, ${statusInfo.color} 0%, ${statusInfo.color}dd 100%)`,
        borderRadius: 24,
        padding: '40px 32px',
        color: '#fff',
        marginBottom: 32,
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '8px 16px',
            borderRadius: 20,
            fontSize: 14,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            {statusInfo.icon} {statusInfo.label}
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '6px 12px',
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600
          }}>
            {tournament.sport}
          </div>
        </div>

        <h1 style={{
          fontSize: 36,
          fontWeight: 900,
          margin: 0,
          marginBottom: 12,
          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}>
          {tournament.name}
        </h1>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          opacity: 0.9
        }}>
          <div>
            <div style={{ fontSize: 14, marginBottom: 4 }}>주최</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>🏢 {tournament.organizerName}</div>
          </div>
          <div>
            <div style={{ fontSize: 14, marginBottom: 4 }}>참가자</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              👥 {tournament.participants?.length || 0}/{tournament.maxParticipants}명
            </div>
          </div>
          <div>
            <div style={{ fontSize: 14, marginBottom: 4 }}>대회 시작</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              📅 {formatDate(tournament.startDate)}
              {daysUntilStart !== null && daysUntilStart > 0 && (
                <span style={{
                  fontSize: 12,
                  background: 'rgba(255,255,255,0.3)',
                  padding: '2px 8px',
                  borderRadius: 10,
                  marginLeft: 8
                }}>
                  D-{daysUntilStart}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 액션 버튼 */}
      {user && (
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 24,
          marginBottom: 32,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9',
          textAlign: 'center'
        }}>
          {isParticipating() ? (
            <div>
              <div style={{
                fontSize: 18,
                fontWeight: 600,
                color: '#10b981',
                marginBottom: 12
              }}>
                ✅ 이미 참가 신청하셨습니다
              </div>
              <div style={{
                fontSize: 14,
                color: '#6b7280',
                marginBottom: 16
              }}>
                대회 시작까지 기다려주세요!
              </div>
              <button
                onClick={() => navigate(`/tournament/${tournament.id}/results`)}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 12,
                  padding: '12px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginRight: 12
                }}
              >
                📊 결과 보기
              </button>
            </div>
          ) : pendingApproval ? (
            <div>
              <div style={{
                fontSize: 18,
                fontWeight: 600,
                color: '#f59e0b',
                marginBottom: 12
              }}>
                ⏳ 승인 대기중입니다
              </div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                관리자의 승인을 기다려주세요.
              </div>
            </div>
          ) : canRegister() ? (
            <div>
              <div style={{
                fontSize: 18,
                fontWeight: 600,
                color: '#667eea',
                marginBottom: 12
              }}>
                🚀 지금 참가 신청하세요!
              </div>
              {daysUntilRegEnd !== null && daysUntilRegEnd > 0 && (
                <div style={{
                  fontSize: 14,
                  color: '#f59e0b',
                  marginBottom: 16
                }}>
                  ⏰ 접수 마감까지 {daysUntilRegEnd}일 남았습니다
                </div>
              )}
              <button
                onClick={() => navigate(`/tournament/${tournament.id}/register`)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  padding: '16px 32px',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={e => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                }}
                onMouseOut={e => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                }}
              >
                📝 참가 신청하기
              </button>
            </div>
          ) : (
            <div>
              <div style={{
                fontSize: 18,
                fontWeight: 600,
                color: '#ef4444',
                marginBottom: 12
              }}>
                ❌ 참가 신청이 불가능합니다
              </div>
              <div style={{
                fontSize: 14,
                color: '#6b7280'
              }}>
                {tournament.status !== 'registration' 
                  ? '모집 기간이 아닙니다' 
                  : '모집이 마감되었습니다'}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: 32
      }}>
        {/* 메인 정보 */}
        <div>
          {/* 대회 정보 */}
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 32,
            marginBottom: 24,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 20,
              color: '#1f2937',
              borderBottom: '2px solid #f3f4f6',
              paddingBottom: 12
            }}>
              📋 대회 소개
            </h3>
            
            <div style={{
              fontSize: 16,
              lineHeight: 1.7,
              color: '#374151',
              marginBottom: 24,
              whiteSpace: 'pre-wrap'
            }}>
              {tournament.description}
            </div>

            {tournament.rules && (
              <>
                <h4 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 16,
                  color: '#1f2937'
                }}>
                  📜 대회 규칙 및 주의사항
                </h4>
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 16,
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: '#374151',
                  whiteSpace: 'pre-wrap'
                }}>
                  {tournament.rules}
                </div>
              </>
            )}
          </div>

          {/* 참가자 목록 */}
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 32,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 20,
              color: '#1f2937',
              borderBottom: '2px solid #f3f4f6',
              paddingBottom: 12
            }}>
              👥 참가자 목록 ({tournament.participants?.length || 0}명)
            </h3>

            {tournament.participants?.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: 40,
                color: '#9ca3af'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>👤</div>
                <div>아직 참가자가 없습니다</div>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 16
              }}>
                {tournament.participants?.map((participant, index) => (
                  <div
                    key={participant.userId || index}
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: 16,
                      textAlign: 'center'
                    }}
                  >
                    <div style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#1f2937',
                      marginBottom: 4
                    }}>
                      {participant.name || participant.nickname}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: '#6b7280'
                    }}>
                      {participant.team || '개인'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 사이드바 정보 */}
        <div>
          {/* 대회 상세 정보 */}
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 24,
            marginBottom: 24,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h4 style={{
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 20,
              color: '#1f2937'
            }}>
              📊 대회 정보
            </h4>

            <div style={{ marginBottom: 24 }}>
              {[
                { icon: '🏆', label: '대회 방식', value: tournament.type },
                { icon: '👶', label: '연령대', value: tournament.ageGroup },
                { icon: '⭐', label: '실력 수준', value: tournament.skillLevel },
                { icon: '👥', label: '최대 참가자', value: `${tournament.maxParticipants}명` },
                { icon: '💰', label: '참가비', value: tournament.entryFee > 0 ? `${tournament.entryFee.toLocaleString()}원` : '무료' }
              ].map((info, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: index < 4 ? '1px solid #f1f5f9' : 'none'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    color: '#6b7280'
                  }}>
                    <span>{info.icon}</span>
                    <span>{info.label}</span>
                  </div>
                  <div style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#1f2937'
                  }}>
                    {info.value}
                  </div>
                </div>
              ))}
            </div>

            {tournament.prizeMoney && (
              <div style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: 12,
                padding: 16,
                marginBottom: 16
              }}>
                <div style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#92400e',
                  marginBottom: 4
                }}>
                  🏆 상금 정보
                </div>
                <div style={{
                  fontSize: 13,
                  color: '#78350f'
                }}>
                  {tournament.prizeMoney}
                </div>
              </div>
            )}
          </div>

          {/* 일정 정보 */}
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 24,
            marginBottom: 24,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h4 style={{
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 20,
              color: '#1f2937'
            }}>
              📅 일정
            </h4>

            <div>
              {[
                { 
                  icon: '📝', 
                  label: '접수 시작', 
                  value: formatDateTime(tournament.registrationStart),
                  color: '#3b82f6'
                },
                { 
                  icon: '⏰', 
                  label: '접수 마감', 
                  value: formatDateTime(tournament.registrationEnd),
                  color: '#f59e0b'
                },
                { 
                  icon: '🚀', 
                  label: '대회 시작', 
                  value: formatDate(tournament.startDate),
                  color: '#10b981'
                },
                { 
                  icon: '🏁', 
                  label: '대회 종료', 
                  value: formatDate(tournament.endDate),
                  color: '#6b7280'
                }
              ].map((schedule, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 0',
                  borderBottom: index < 3 ? '1px solid #f1f5f9' : 'none'
                }}>
                  <div style={{
                    background: `${schedule.color}15`,
                    color: schedule.color,
                    padding: '6px',
                    borderRadius: 8,
                    fontSize: 14
                  }}>
                    {schedule.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 12,
                      color: '#6b7280',
                      marginBottom: 2
                    }}>
                      {schedule.label}
                    </div>
                    <div style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#1f2937'
                    }}>
                      {schedule.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 장소 정보 */}
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: 24,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f1f5f9'
          }}>
            <h4 style={{
              fontSize: 18,
              fontWeight: 700,
              marginBottom: 20,
              color: '#1f2937'
            }}>
              📍 장소
            </h4>

            <div style={{
              marginBottom: 16
            }}>
              <div style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#1f2937',
                marginBottom: 8
              }}>
                {tournament.venue}
              </div>
              <div style={{
                fontSize: 14,
                color: '#6b7280',
                marginBottom: 8
              }}>
                {tournament.venueAddress}
              </div>
              <div style={{
                fontSize: 12,
                color: '#9ca3af'
              }}>
                {tournament.sido} {tournament.sigungu}
              </div>
            </div>

            {tournament.contactInfo && (
              <div style={{
                background: '#f0f9ff',
                border: '1px solid #3b82f6',
                borderRadius: 12,
                padding: 16
              }}>
                <div style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#1e40af',
                  marginBottom: 4
                }}>
                  📞 문의처
                </div>
                <div style={{
                  fontSize: 13,
                  color: '#1e3a8a'
                }}>
                  {tournament.contactInfo}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 하단 네비게이션 */}
      <div style={{
        background: '#fff',
        borderRadius: 20,
        padding: 24,
        marginTop: 32,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f1f5f9',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'flex',
          gap: 16,
          justifyContent: 'center'
        }}>
          <button
            onClick={() => navigate('/tournaments')}
            style={{
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={e => e.target.style.background = '#e5e7eb'}
            onMouseOut={e => e.target.style.background = '#f3f4f6'}
          >
            ← 대회 목록으로
          </button>
          
          {tournament.status === 'active' || tournament.status === 'completed' ? (
            <button
              onClick={() => navigate(`/tournament/${tournament.id}/results`)}
              style={{
                background: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '12px 24px',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={e => e.target.style.background = '#5a67d8'}
              onMouseOut={e => e.target.style.background = '#667eea'}
            >
              📊 결과 보기
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default TournamentDetail;